import Image from 'next/image';
import Link from 'next/link';
import type { TFunction } from 'next-i18next';
import { Trans } from 'next-i18next';
import { FaInstagram, FaTiktok, FaTwitch, FaXTwitter, FaYoutube } from 'react-icons/fa6';

import AffiliatesEnum from '@/enums/affiliates-enum';

const JoysTickIcon = () => (
  <svg width='35' height='25' viewBox='0 0 35 25' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path d='M30.3555 3.24288C29.0482 1.08096 26.6514 0 24.0367 0H10.9633C8.34863 0 5.95185 1.08096 4.64451 3.24288C-0.366953 10.8096 -1.4564 22.2677 2.02983 24.6458C5.51607 27.024 13.3601 16.6468 17.5 16.6468C21.6399 16.6468 29.266 27.024 32.9702 24.6458C36.4564 22.2677 35.367 10.8096 30.3555 3.24288ZM13.1422 10.8096H10.9633V12.9715H8.78441V10.8096H6.60552V8.64767H8.78441V6.48575H10.9633V8.64767H13.1422V10.8096ZM24.9082 11.8905C24.9082 12.9715 24.0367 13.8363 22.9472 13.8363C21.8578 13.8363 20.9862 12.9715 20.9862 11.8905C20.9862 10.8096 21.8578 9.94482 22.9472 9.94482C24.0367 9.94482 24.9082 10.8096 24.9082 11.8905ZM29.0482 7.56671C29.0482 8.64767 28.1766 9.51243 27.0871 9.51243C25.9977 9.51243 25.1261 8.64767 25.1261 7.56671C25.1261 6.48575 25.9977 5.62098 27.0871 5.62098C28.1766 5.62098 29.0482 6.48575 29.0482 7.56671Z' fill='white' />
  </svg>
);

interface InfluencersProps {
  t: TFunction;
  isDisabledBackGround?: boolean;
}

const Influencers = ({ t, isDisabledBackGround = true }: InfluencersProps) => {
  const backgroundClass = isDisabledBackGround ? '' : 'bg-[url(/assets/images/trial/new/bg-second-section.webp)] bg-cover bg-center bg-no-repeat';
  const affiliates = Object.fromEntries(
    AffiliatesEnum({ customList: false })
      .filter((affiliate) => affiliate.name.toUpperCase() === 'COLDZERA' || affiliate.name.toUpperCase() === 'SACY' || affiliate.name.toUpperCase() === 'SUJA')
      .map((affiliate) => [affiliate.name.toUpperCase(), affiliate])
  );

  return (
    <section className='mx-auto mb-10 flex w-full max-w-[1400px] flex-col items-center justify-center py-20'>
      <h3 className='text-center text-5xl font-bold uppercase text-white tablet:text-7xl'>
        <Trans i18nKey='who_use_noping' components={{ span: <span className='text-[#93FF18]' /> }}>
          {t('who_use_noping', { ns: 'components/player-box' })}
        </Trans>
      </h3>
      <div className={`w-full ${backgroundClass}`}>
        <div className='mx-auto flex w-full max-w-[1320px] flex-col items-baseline justify-center gap-8 px-5 tablet:gap-10 laptop:flex-row'>
          {/* CARD COLDZERA */}
          <div className='mx-auto flex w-full max-w-[642px] flex-col'>
            <div className='w-full'>
              <Image src='/assets/images/trial/new/card-coldzera-bg-black.webp' alt='Coldzera image' height={342} width={641} />
            </div>

            <div className='relative flex w-full flex-col items-center justify-center gap-5 rounded-b-[50px] bg-[#1D2028] px-10'>
              <div className='flex w-full flex-col items-center justify-start text-white'>
                <div className='mt-2 flex w-full flex-col mobile:flex-row mobile:items-center mobile:gap-5'>
                  <Image src='/assets/images/trial/new/country/pt.webp' width={64} height={40} alt='Icone bandeira do brasil' className='max-mobile:h-8 max-mobile:w-10' />
                  <div className='flex items-center gap-2'>
                    <p className='text-5xl font-bold uppercase text-white sm:text-8xl'>coldzera</p>
                    <p className='text-base font-medium mobile:text-lg'>@coldzera</p>
                  </div>
                </div>
                <div className='w-full rounded-[100px] bg-[#93FF18]'>
                  <p className='text-center text-base font-bold uppercase text-black mobile:text-xl'>{t('best_world_cs', { ns: 'components/player-box' })}</p>
                </div>
              </div>

              <div>
                <p className='text-base font-normal leading-[120%] text-white'>{t('influencers.coldzera', { ns: 'components/player-box' })}</p>
              </div>

              <div className='mb-4 flex w-full items-center justify-between border-t-2 border-white pt-2'>
                <div className='flex items-center gap-2'>
                  <JoysTickIcon />
                  <p className='text-lg font-bold uppercase text-white mobile:text-4xl'>CS2</p>
                </div>
                <div className='ms-auto flex gap-3'>
                  <Link href={affiliates.COLDZERA?.social_midia.instagram ?? ''} target='_blank' rel='noopener noreferrer' className='text-base font-bold text-white hover:opacity-80'>
                    <FaInstagram height={23} width={23} />
                  </Link>
                  <Link href={affiliates.COLDZERA?.social_midia.x ?? ''} target='_blank' rel='noopener noreferrer' className='text-base font-bold text-white hover:opacity-80'>
                    <FaXTwitter />
                  </Link>
                  <Link href={affiliates.COLDZERA?.social_midia.youtube ?? ''} target='_blank' rel='noopener noreferrer' className='text-base font-bold text-white hover:opacity-80'>
                    <FaYoutube width={24} height={18} />
                  </Link>
                  <Link href={affiliates.COLDZERA?.social_midia.twitch ?? ''} target='_blank' rel='noopener noreferrer' className='text-base font-bold text-white hover:opacity-80'>
                    <FaTwitch width={23} height={24} />
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className='flex w-full flex-col items-center justify-center gap-8 laptop:items-stretch'>
            {/* CARD SUJA */}
            <div className='w-full max-w-[642px] rounded-t-[50px] bg-[#1D2028] tablet:h-full tablet:max-h-[283px]'>
              <div className='flex w-full flex-col items-center gap-7 tablet:flex-row tablet:items-stretch tablet:justify-center'>
                <div className="h-[267px] w-[247px] bg-[url('/assets/images/trial/new/card-suja.webp')] bg-center bg-no-repeat tablet:bg-cover" />
                <div className='flex flex-col justify-around gap-4 px-2.5 max-tablet:w-9/12 tablet:max-w-[340px]'>
                  <div className='flex flex-col gap-2'>
                    <div className='flex items-center gap-2'>
                      <Image src='/assets/images/trial/new/country/es.webp' width={31} height={23} alt='Icone bandeira espanha' />
                      <p className='text-2xl font-bold text-white'>SUJA</p>
                      <p className='text-sm font-normal text-white'>@suja_gg</p>
                    </div>
                    <div className='w-full rounded-[100px] bg-[#93FF18]'>
                      <p className='text-center text-xs font-bold uppercase text-black'>{t('influencer', { ns: 'components/player-box' })}</p>
                    </div>
                    <div className='mt-2'>
                      <p className='text-sm font-normal leading-[120%] text-white'>{t('influencers.suja', { ns: 'components/player-box' })}</p>
                    </div>
                  </div>

                  <div className='mb-4 flex items-center justify-between border-t-2 border-white pt-2'>
                    <div className=''>
                      <p className='text-sm font-bold uppercase leading-[140%] text-white'>fortnite</p>
                    </div>
                    <div className='ms-auto flex gap-3'>
                      <Link href={affiliates.SUJA?.social_midia.instagram ?? ''} target='_blank' rel='noopener noreferrer' className='text-base font-bold text-white hover:opacity-80'>
                        <FaInstagram height={23} width={23} />
                      </Link>
                      <Link href={affiliates.SUJA?.social_midia.x ?? ''} target='_blank' rel='noopener noreferrer' className='text-base font-bold text-white hover:opacity-80'>
                        <FaXTwitter />
                      </Link>
                      <Link href={affiliates.SUJA?.social_midia.tiktok ?? ''} target='_blank' rel='noopener noreferrer' className='text-base font-bold text-white hover:opacity-80'>
                        <FaTiktok width={19} height={22} />
                      </Link>
                      <Link href={affiliates.SUJA?.social_midia.youtube ?? ''} target='_blank' rel='noopener noreferrer' className='text-base font-bold text-white hover:opacity-80'>
                        <FaYoutube width={24} height={18} />
                      </Link>
                      <Link href={affiliates.SUJA?.social_midia.twitch ?? ''} target='_blank' rel='noopener noreferrer' className='text-base font-bold text-white hover:opacity-80'>
                        <FaTwitch width={23} height={24} />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* CARD SACY */}
            <div className='w-full max-w-[642px] rounded-b-[50px] bg-[#1D2028] tablet:h-full tablet:max-h-[283px]'>
              <div className='flex w-full flex-col items-center gap-7 pt-4 tablet:flex-row tablet:items-stretch tablet:justify-center'>
                <div className="h-[264px] w-[247px] bg-[url('/assets/images/trial/new/sacy-card.webp')] bg-bottom bg-no-repeat" />
                <div className='flex flex-col justify-around gap-4 px-2.5 max-tablet:w-9/12 tablet:max-w-[340px]'>
                  <div className='flex flex-col gap-2'>
                    <div className='flex items-center gap-2'>
                      <Image src='/assets/images/trial/new/country/pt-30x23.webp' width={31} height={23} alt='Icone bandeira Brasil' />
                      <p className='text-2xl font-bold text-white'>SACY</p>
                      <p className='text-sm font-normal text-white'>@sacy</p>
                    </div>
                    <div className='w-full rounded-[100px] bg-[#93FF18]'>
                      <p className='text-center text-xs font-bold uppercase text-black'>{t('valorant_champion', { ns: 'components/player-box' })}</p>
                    </div>
                    <div className='mt-2'>
                      <p className='text-sm font-normal leading-[120%] text-white'>{t('influencers.sacy', { ns: 'components/player-box' })}</p>
                    </div>
                  </div>

                  <div className='mb-4 flex items-center justify-between border-t-2 border-white pt-2'>
                    <div className=''>
                      <p className='text-sm font-bold uppercase leading-[140%] text-white'>VALORANT</p>
                    </div>
                    <div className='ms-auto flex gap-3'>
                      <Link href={affiliates.SACY?.social_midia.instagram ?? ''} target='_blank' rel='noopener noreferrer' className='text-base font-bold text-white hover:opacity-80'>
                        <FaInstagram height={23} width={23} />
                      </Link>
                      <Link href={affiliates.SACY?.social_midia.x ?? ''} target='_blank' rel='noopener noreferrer' className='text-base font-bold text-white hover:opacity-80'>
                        <FaXTwitter />
                      </Link>
                      <Link href={affiliates.SACY?.social_midia.tiktok ?? ''} target='_blank' rel='noopener noreferrer' className='text-base font-bold text-white hover:opacity-80'>
                        <FaTiktok width={19} height={22} />
                      </Link>
                      <Link href={affiliates.SACY?.social_midia.youtube ?? ''} target='_blank' rel='noopener noreferrer' className='text-base font-bold text-white hover:opacity-80'>
                        <FaYoutube width={24} height={18} />
                      </Link>
                      <Link href={affiliates.SACY?.social_midia.twitch ?? ''} target='_blank' rel='noopener noreferrer' className='text-base font-bold text-white hover:opacity-80'>
                        <FaTwitch width={23} height={24} />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export { Influencers };
